import { useEffect, useRef, useState } from 'react';

// Live updates between people on the app at the same time — the client side of
// server/services/live.ts. One EventSource per tab:
//   - `change` events (something was saved by someone else) → useLiveChanges;
//   - `presence` events (who else is on this screen, and which card each one is
//     moving or has open) → usePresence.
// Requests carry this tab's id (X-Live-Conn, see api/client.ts) so its own
// saves don't come back to it as changes.

export interface LiveChange { area: 'tasks' | 'project' | 'kanban'; projectId?: number | null; boardId?: number | null; actor?: string | null }
// over: while dragging, the cell (task board) or column (Kanban Fonction) the card is over —
// the others see a ghost of the card follow it there.
export interface LiveFocus { type: 'task' | 'card'; id: number; action: 'drag' | 'open' | 'moved'; over?: string }
export interface LivePerson { userId: number; name: string; focus: LiveFocus | null }
// Someone's pointer: where it is inside the element marked data-live-anchor="…" (fractions of its size).
export interface LiveCursor { userId: number; name: string; anchor: string | null; fx: number; fy: number }

let source: EventSource | null = null;
let conn: string | null = null;
let room: string | null = null;
let focus: LiveFocus | null = null;
const changeListeners = new Set<(c: LiveChange) => void>();
const presenceListeners = new Set<(room: string, people: LivePerson[]) => void>();
const cursorListeners = new Set<(c: LiveCursor) => void>();

export const getLiveConn = () => conn;

/** Opens the stream once the user is logged in (the browser reconnects it by itself). */
export function startLive(): void {
  if (source || typeof EventSource === 'undefined') return;
  source = new EventSource('/api/live/stream', { withCredentials: true });
  source.addEventListener('hello', (e) => {
    conn = JSON.parse((e as MessageEvent).data).conn;
    sendPresence(); // after a reconnect too: the server forgot this tab
  });
  source.addEventListener('change', (e) => {
    const change = JSON.parse((e as MessageEvent).data) as LiveChange;
    changeListeners.forEach(fn => fn(change));
  });
  source.addEventListener('cursor', (e) => {
    const c = JSON.parse((e as MessageEvent).data) as LiveCursor;
    cursorListeners.forEach(fn => fn(c));
  });
  source.addEventListener('presence', (e) => {
    const { room: r, people } = JSON.parse((e as MessageEvent).data);
    presenceListeners.forEach(fn => fn(r, people));
  });
}

export function stopLive(): void {
  source?.close();
  source = null;
  conn = null;
}

function sendPresence(): void {
  if (!conn) return;
  fetch('/api/live/presence', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conn, room, focus }),
  }).catch(() => {});
}

/** The card this tab is moving or has open (null: none) — shown to the others as initials. */
export function setLiveFocus(next: LiveFocus | null): void {
  if (JSON.stringify(next) === JSON.stringify(focus)) return;
  focus = next;
  sendPresence();
}

/**
 * Calls `onChange` when someone else saves something — debounced, so a burst
 * of saves (a drag moves several cards) refetches once.
 */
export function useLiveChanges(onChange: (c: LiveChange) => void, delayMs = 400): void {
  const cb = useRef(onChange);
  cb.current = onChange;
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let pending: LiveChange | null = null;
    const listener = (c: LiveChange) => {
      pending = c;
      clearTimeout(timer);
      timer = setTimeout(() => { if (pending) cb.current(pending); pending = null; }, delayMs);
    };
    changeListeners.add(listener);
    return () => { changeListeners.delete(listener); clearTimeout(timer); };
  }, [delayMs]);
}

/** Puts this tab in `room` while mounted; returns the other people there (not me). */
export function usePresence(roomId: string | null, myUserId: number | undefined): LivePerson[] {
  const [people, setPeople] = useState<LivePerson[]>([]);
  useEffect(() => {
    if (!roomId) return;
    room = roomId;
    focus = null;
    sendPresence();
    const listener = (r: string, list: LivePerson[]) => { if (r === roomId) setPeople(list); };
    presenceListeners.add(listener);
    return () => {
      presenceListeners.delete(listener);
      if (room === roomId) { room = null; focus = null; sendPresence(); }
      setPeople([]);
    };
  }, [roomId]);
  return people.filter(p => p.userId !== myUserId);
}

/** "Sofia Mitev" → "SM". */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return (words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/** Who has a given card in focus: id → people. */
export function focusMap(people: LivePerson[], type: LiveFocus['type']): Map<number, LivePerson[]> {
  const map = new Map<number, LivePerson[]>();
  for (const p of people) {
    if (p.focus?.type !== type) continue;
    map.set(p.focus.id, [...(map.get(p.focus.id) ?? []), p]);
  }
  return map;
}

let movedTimer: ReturnType<typeof setTimeout> | undefined;
/** After a drop: the others see "moved by …" on the card for a few seconds, then `after`. */
export function flashMoved(type: LiveFocus['type'], id: number, after: LiveFocus | null = null): void {
  clearTimeout(movedTimer);
  setLiveFocus({ type, id, action: 'moved' });
  movedTimer = setTimeout(() => {
    if (focus?.action === 'moved' && focus.id === id) setLiveFocus(after);
  }, 4000);
}

/** The people dragging a card over `over` (a cell or column id) — to draw its ghost there. */
export function draggingOver(people: LivePerson[], type: LiveFocus['type'], over: string): LivePerson[] {
  return people.filter(p => p.focus?.type === type && p.focus.action === 'drag' && p.focus.over === over);
}

// This tab's pointer, at most ~8 times a second (the last position always goes out).
let cursorTimer: ReturnType<typeof setTimeout> | undefined;
let cursorPending: { anchor: string | null; fx: number; fy: number } | null = null;
let cursorSentAt = 0;
export function sendCursor(anchor: string | null, fx = 0, fy = 0): void {
  if (!conn || !room) return;
  cursorPending = { anchor, fx, fy };
  const flush = () => {
    if (!cursorPending || !conn) return;
    const body = JSON.stringify({ conn, ...cursorPending });
    cursorPending = null;
    cursorSentAt = Date.now();
    fetch('/api/live/cursor', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body }).catch(() => {});
  };
  clearTimeout(cursorTimer);
  const wait = 125 - (Date.now() - cursorSentAt);
  if (wait <= 0) flush(); else cursorTimer = setTimeout(flush, wait);
}

export function onLiveCursor(fn: (c: LiveCursor) => void): () => void {
  cursorListeners.add(fn);
  return () => { cursorListeners.delete(fn); };
}
